import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { CdsButton } from '@cds/react/button';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder, faFolderOpen, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import "./DeliverablesPublic.css";
import "../App.css"; 

const DeliverabelsPublic = () => {
    const [uploads, setUploads] = useState([]);
    const [expandedFolders, setExpandedFolders] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [folderPosition, setFolderPosition] = useState(null);
    const [expandedFolderId, setExpandedFolderId] = useState(null);
    const [user_nickname, setUserNickname] = useState('');
    const folderRefs = useRef({});
    const { user, getAccessTokenSilently, loginWithRedirect, isAuthenticated } = useAuth0();
    const itemsPerPage = 20;
    const navigate = useNavigate();

    const api = axios.create({
        baseURL: process.env.REACT_APP_API_BASE_URL,
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
              const token = await getAccessTokenSilently({
                authorizationParams: {
                  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                  scope: "read:files read:folders",
                },
              });
      
              const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              };
              
              const folderResponse = await api.get('/api/folders', { headers });
              const singleFileResponse = await api.get('/api/uploads', { headers });
              const folderData = folderResponse.data.folders.map(folder => ({ ...folder, type: 'folder' }));
              const singleFileData = singleFileResponse.data.files.map(file => ({ ...file, type: 'file' }));
              const combinedData = [...folderData, ...singleFileData];
              setUploads(combinedData);
            } catch (error) {
                console.error('Error fetching uploads:', error);
            }
        };

        fetchData();
    }, [api, getAccessTokenSilently, loginWithRedirect]);

    const refreshData = async () => {
        try {
            let headers = {
                'Content-Type': 'application/json',
            };

            if (isAuthenticated) {
                const token = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: "read:files read:folders",
                    },
                });
                headers['Authorization'] = `Bearer ${token}`;
            }

            const folderResponse = await api.get('/api/folders', { headers });
            const singleFileResponse = await api.get('/api/uploads', { headers });
            const folderData = folderResponse.data.folders.map(folder => ({ ...folder, type: 'folder' }));
            const singleFileData = singleFileResponse.data.files.map(file => ({ ...file, type: 'file' }));
            const combinedData = [...folderData, ...singleFileData];
            setUploads(combinedData);
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    useEffect(() => {
        if (user) {
            const nickname = user.nickname.replace('.', ' ').replace(/\b\w/g, char => char.toUpperCase());
            setUserNickname(nickname);
        }
    }, [user]);

    const handleFileClick = (upload) => {
        if (upload.type === 'folder') {
            handleFolderClick(upload.id);
            return;
        }

        // If the file is a PDF, navigate to the PDF viewer
        if (upload.file_key && upload.file_key.toLowerCase().endsWith('.pdf')) {
            navigate(`/view-pdf/${upload.id}/${upload.file_key}`);
        } else if (upload.file_url) {
            // For all other file extensions, download the file
            const link = document.createElement('a');
            link.href = upload.file_url;
            link.download = upload.file_key || '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            console.warn('Unsupported file type:', upload.file_key);
        }
    };

    const handleFolderClick = (folderId) => {
        const isExpanding = !expandedFolders[folderId];
        
        setExpandedFolders((prevExpandedFolders) => ({
            ...prevExpandedFolders,
            [folderId]: isExpanding,
        }));

        if (isExpanding) {
            // Calculate position for portal
            const folderElement = folderRefs.current[folderId];
            if (folderElement) {
                const rect = folderElement.getBoundingClientRect();
                setFolderPosition({
                    top: rect.bottom + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width
                });
                setExpandedFolderId(folderId);
            }
        } else {
            setFolderPosition(null);
            setExpandedFolderId(null);
        }
    };

    const handleFileDownload = (file) => {
        if (file.file_url) {
            const link = document.createElement('a');
            link.href = file.file_url;
            link.download = file.file_key || '';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const handleDeleteClick = async (id, type, author, nickname) => {
        if (nickname !== author) {
            return;
        }
        
        const itemType = type === 'folder' ? 'folder (including all subfolders and files)' : 'file';
        const confirmDelete = window.confirm(`Are you sure you want to delete this ${itemType}? This action cannot be undone.`);
        
        if (confirmDelete) {
            try {
                const token = await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: 'read:files create:files delete:files read:folders delete:folders',
                    }
                });
                const headers = { Authorization: `Bearer ${token}` };
                
                const response = await api.delete(`/api/uploads/${id}`, { headers });
                
                // Show success message with details
                if (type === 'folder' && response.data.deletedFolderCount) {
                    alert(`Successfully deleted folder with ${response.data.deletedFolderCount} total folders and ${response.data.files.length} files.`);
                } else {
                    alert('Item deleted successfully.');
                }
                
                // Refresh data (use existing refresh function for each page)
                await refreshData();
                
            } catch (error) {
                console.error('Error deleting item:', error);
                alert('Error deleting item. Please try again.');
            }
        }
    };

    const handlePreviousPage = () => {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setCurrentPage((prevPage) => prevPage + 1);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
        setCurrentPage(1);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
        setCurrentPage(1);
    };

    const filteredUploads = uploads.filter((upload) => {
        if (upload.type === 'folder') {
            const matchesSearchTerm = upload.folder_name ? upload.folder_name.toLowerCase().includes(searchTerm.toLowerCase()) : false;
            const isPublic = upload.is_public === true;
            const isDeliverable = upload.is_deliverable === true;
            return matchesSearchTerm && isPublic && isDeliverable;
        }

        const uploadDate = new Date(upload.upload_date);
        const isWithinDateRange =
            (!startDate || uploadDate >= new Date(startDate)) &&
            (!endDate || uploadDate <= new Date(endDate));
        const matchesSearchTerm =
            (upload.category && upload.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (upload.country && upload.country.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (upload.author && upload.author.toLowerCase().includes(searchTerm.toLowerCase()));
        const isPublic = upload.is_public === true;
        const isDeliverable = upload.is_deliverable === true;
        return isWithinDateRange && matchesSearchTerm && isPublic && isDeliverable;
    });

    const sortedUploads = filteredUploads.sort((a, b) => new Date(b.upload_date) - new Date(a.upload_date));

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedUploads = sortedUploads.slice(startIndex, endIndex);

    return (
        <div className='search-main-div'>
            <h1 className='search-h1'>Search Public Deliverables</h1>
            <div>
                <input className='search-input'
                    type="text"
                    placeholder="Search by Title, country, or author"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    style={{ marginBottom: '20px', padding: '10px', width: '100%' }}
                />
                <div style={{ marginBottom: '20px' }}>
                    <label>
                        Start Date:
                        <input
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                            style={{ marginLeft: '10px', marginRight: '20px' }}
                        />
                    </label>
                    <label>
                        End Date:
                        <input
                            type="date"
                            value={endDate}
                            onChange={handleEndDateChange}
                            style={{ marginLeft: '10px' }}
                        />
                    </label>
                </div>
                <div className="list-header" style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <span style={{ flex: '2' }}>Title</span>
                    <span style={{ flex: '2' }}>Author</span>
                    <span style={{ flex: '0.75' }}>Upload Date</span>
                    <span style={{ flex: '0.75' }}>File Type</span>
                    <span style={{ flex: '0.75', textAlign: 'right' }}>Country</span>
                </div>
                <ol>
                    {paginatedUploads.map((upload, index) => (
                        <React.Fragment key={index}>
                            <li className="search-list-item" 
                                ref={upload.type === 'folder' ? (el) => folderRefs.current[upload.id] = el : null}
                                onClick={() => handleFileClick(upload)}>
                                <div className='list-info' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <p style={{ flex: '2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        {upload.type === 'folder' ? (
                                            <>
                                                <FontAwesomeIcon 
                                                    icon={expandedFolders[upload.id] ? faFolderOpen : faFolder} 
                                                    style={{ color: '#007acc', fontSize: '16px' }} 
                                                />
                                                {upload.folder_name && upload.folder_name.length > 65 ? `${upload.folder_name.substring(0, 65)}...` : upload.folder_name}
                                            </>
                                        ) : (
                                            upload.category && upload.category.length > 65 ? `${upload.category.substring(0, 65)}...` : upload.category
                                        )}
                                    </p>
                                    <p style={{ flex: '2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {upload.type === 'folder' ? (upload.author || 'N/A') : (upload.author && upload.author.length > 60 ? `${upload.author.substring(0, 60)}...` : upload.author)}
                                    </p>
                                    <p style={{ flex: '0.75' }}>
                                        {upload.type === 'folder' ? (upload.created_at ? new Date(upload.created_at).toLocaleDateString() : 'N/A') : new Date(upload.upload_date).toLocaleDateString()}
                                    </p>
                                    <p style={{ flex: '0.75' }}>
                                        {upload.type === 'folder' ? 'Folder' : (upload.file_url ? upload.file_url.split('.').pop() : 'N/A')}
                                    </p>
                                    <p style={{ flex: '0.75' }}>
                                        {upload.country || 'N/A'}
                                    </p>
                                </div>
                            </li>
                        </React.Fragment>
                    ))}
                </ol>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '5px' }}>
                    <CdsButton onClick={handlePreviousPage} disabled={currentPage === 1}>
                        Previous
                    </CdsButton>
                    <CdsButton onClick={handleNextPage} disabled={endIndex >= sortedUploads.length}>
                        Next
                    </CdsButton>
                </div>
            </div>
            {folderPosition && expandedFolderId && createPortal(
                <div className="folder-portal-container" style={{
                    position: 'absolute',
                    top: folderPosition.top,
                    left: Math.max(20, Math.min(50, (window.innerWidth - 800) / 2)),
                    width: Math.min(800, window.innerWidth - 40),
                    minWidth: '600px',
                    zIndex: 10000,
                    background: 'linear-gradient(135deg, #296783, #1e4d5f)',
                    border: '2px solid #4a90a4',
                    borderRadius: '15px',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                    padding: '20px',
                    maxHeight: '500px',
                    overflowY: 'auto'
                }}>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                        {uploads.find(item => item.id === expandedFolderId)?.files
                            ?.filter(file => {
                                const fileDate = new Date(file.upload_date);
                                const isWithinDateRange =
                                    (!startDate || fileDate >= new Date(startDate)) &&
                                    (!endDate || fileDate <= new Date(endDate));
                                const matchesSearchTerm =
                                    (file.category && file.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (file.country && file.country.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (file.author && file.author.toLowerCase().includes(searchTerm.toLowerCase()));
                                const isPublic = file.is_public === true;
                                const isDeliverable = file.is_deliverable === true;
                                return isWithinDateRange && matchesSearchTerm && isPublic && isDeliverable;
                            })
                            ?.map((file, fileIndex) => (
                                <li key={fileIndex} 
                                    onClick={() => handleFileClick(file)}
                                    className="folder-file-item"
                                    style={{ 
                                        backgroundColor: 'rgba(209, 221, 162, 0.9)',
                                        borderRadius: '8px',
                                        margin: '8px 0',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        border: '1px solid rgba(74, 144, 164, 0.3)',
                                        transition: 'all 0.2s ease',
                                        backdropFilter: 'blur(2px)'
                                    }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                                        <p style={{ 
                                            flex: '3', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            margin: 0,
                                            fontWeight: '500',
                                            minWidth: '200px'
                                        }}>
                                            {(() => {
                                                try {
                                                    let filename = file.file || file.file_key?.split('/').pop() || file.file_url?.split('/').pop();
                                                    if (!filename) return file.category || 'Unknown file';
                                                    filename = filename.replace(/^\d+_/, '');
                                                    filename = filename
                                                        .replace(/%C3%83%C2%85/g, 'Å')
                                                        .replace(/%C3%83%C2%84/g, 'Ä')
                                                        .replace(/%C3%83%C2%96/g, 'Ö')
                                                        .replace(/%C3%83%C2%A4/g, 'ä')
                                                        .replace(/%C3%83%C2%A5/g, 'å')
                                                        .replace(/%C3%83%C2%B6/g, 'ö')
                                                        .replace(/%C3%A5/g, 'å')
                                                        .replace(/%C3%A4/g, 'ä')
                                                        .replace(/%C3%B6/g, 'ö')
                                                        .replace(/Ã¥/g, 'å')
                                                        .replace(/Ã¤/g, 'ä')
                                                        .replace(/Ã¶/g, 'ö')
                                                        .replace(/Ã…/g, 'Å')
                                                        .replace(/Ã„/g, 'Ä')
                                                        .replace(/Ã–/g, 'Ö');
                                                    return filename.length > 80 ? `${filename.substring(0, 80)}...` : filename;
                                                } catch (e) {
                                                    return file.category || file.file_key?.split('/').pop() || 'Unknown file';
                                                }
                                            })()}
                                        </p>
                                        <p style={{ 
                                            flex: '1.5', 
                                            whiteSpace: 'nowrap', 
                                            overflow: 'hidden', 
                                            textOverflow: 'ellipsis', 
                                            margin: 0,
                                            fontSize: '0.9em',
                                            color: '#2582b0'
                                        }}>
                                            {file.author && file.author.length > 25 ? `${file.author.substring(0, 25)}...` : file.author}
                                        </p>
                                        <p style={{ 
                                            flex: '1', 
                                            margin: 0,
                                            fontSize: '0.85em',
                                            color: '#2582b0',
                                            textAlign: 'center'
                                        }}>
                                            {new Date(file.upload_date).toLocaleDateString()}
                                        </p>
                                        <p style={{ 
                                            flex: '0.5', 
                                            margin: 0,
                                            fontSize: '0.8em',
                                            color: '#2582b0',
                                            textAlign: 'center',
                                            textTransform: 'uppercase',
                                            fontWeight: 'bold'
                                        }}>
                                            {file.file_url ? file.file_url.split('.').pop() : 'N/A'}
                                        </p>
                                        <p style={{ 
                                            flex: '0.8', 
                                            margin: 0,
                                            fontSize: '0.8em',
                                            color: '#2582b0',
                                            textAlign: 'center'
                                        }}>
                                            {file.country || 'N/A'}
                                        </p>
                                        {user_nickname === file.author && (
                                            <FontAwesomeIcon 
                                                className='delete' 
                                                icon={faTrashAlt} 
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    handleDeleteClick(file.id, file.type, file.author, user_nickname);
                                                }} 
                                                style={{ cursor: 'pointer', color: 'red', marginLeft: '10px' }} 
                                            />
                                        )}
                                    </div>
                                </li>
                            ))}
                    </ul>
                </div>,
                document.body
            )}
        </div>
    );
};

export default DeliverabelsPublic;